/**
 * Geolocation + weather helpers (client-side, keyless APIs).
 * Self-contained copy for the redesign app (avoids Turbopack parent imports).
 */

import { weatherKindOf, type WeatherKind } from "./weather-conditions";

export type Located = {
  lat: number;
  lon: number;
  place: string;
  /**
   * ISO country code from the live IP service, e.g. "PK". Absent when the
   * provider did not return one.
   *
   * This exists because the server's offline geoip database and these live
   * services disagree, and the live one is right. A visitor in Rawalpindi was
   * shown the international helpline because the bundled database resolved
   * their ISP's range to another country, while this lookup - the one already
   * powering the weather strip - correctly said Pakistan. The helpline now
   * reads this instead. See useIsInternationalVisitor below.
   */
  countryCode?: string;
};
export type CurrentWeather = {
  temp: number;
  label: string;
  /** Which icon to draw — see greenpak/weather-conditions.ts. */
  kind: WeatherKind;
  windKph?: number;
};


const IP_LOCATION_CACHE_KEY = "pbd_ip_location_cache_v2";
// A visitor's IP-derived location doesn't meaningfully change minute to
// minute, but locateByIp() is called on every WeatherBar/MapAtlas mount -
// every page nav within a session otherwise re-hit ipwho.is (and its
// geojs.io fallback) for the same answer.
const IP_LOCATION_CACHE_TTL_MS = 20 * 60 * 1000;

function readCachedIpLocation(): Located | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(IP_LOCATION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { value: Located; expiresAt: number };
    return parsed.expiresAt > Date.now() ? parsed.value : null;
  } catch {
    return null;
  }
}

function writeCachedIpLocation(value: Located) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      IP_LOCATION_CACHE_KEY,
      JSON.stringify({ value, expiresAt: Date.now() + IP_LOCATION_CACHE_TTL_MS }),
    );
  } catch {
    // storage full/unavailable/disabled — soft-fail, just skip caching this time
  }
}

async function fetchIpLocation(): Promise<Located | null> {
  try {
    const g = await fetch("https://ipwho.is/").then((r) => r.json());
    if (g?.success && typeof g.latitude === "number") {
      return {
        lat: g.latitude,
        lon: g.longitude,
        place: g.city || g.country || "",
        countryCode: typeof g.country_code === "string" ? g.country_code : undefined,
      };
    }
  } catch (err) {
    console.warn("geo: ipwho.is failed.", err);
  }
  try {
    const g = await fetch("https://get.geojs.io/v1/ip/geo.json").then((r) => r.json());
    const lat = parseFloat(g?.latitude);
    const lon = parseFloat(g?.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return {
        lat,
        lon,
        place: g.city || g.country || "",
        countryCode: typeof g.country_code === "string" ? g.country_code : undefined,
      };
    }
  } catch (err) {
    console.warn("geo: geojs.io failed.", err);
  }
  return null;
}

export async function locateByIp(): Promise<Located | null> {
  const cached = readCachedIpLocation();
  if (cached) return cached;

  const located = await fetchIpLocation();
  if (located) writeCachedIpLocation(located);
  return located;
}

export function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Our /api/weather route caps each request at 80 coordinates. The map can have
// far more pins than that, so split into chunks that stay under the cap and fetch
// them in parallel, otherwise the whole batch is rejected and no weather shows.
const WEATHER_CHUNK = 60;

async function fetchWeatherChunk(
  coords: { lat: number; lng: number }[],
): Promise<(CurrentWeather | null)[]> {
  const lat = coords.map((c) => c.lat).join(",");
  const lng = coords.map((c) => c.lng).join(",");
  try {
    const res = await fetch(`/api/weather?lat=${lat}&lng=${lng}`).catch(() => null);
    if (res?.ok) {
      const data = (await res.json()) as { conditions?: (CurrentWeather | null)[] };
      if (data.conditions?.length === coords.length) {
        // weatherKindOf also covers responses cached before `kind` existed.
        return data.conditions.map((c) => (c ? { ...c, kind: weatherKindOf(c) } : null));
      }
    }
  } catch (err) {
    console.warn("geo: weather chunk via API failed.", err);
  }
  return coords.map(() => null);
}

export async function weatherForMany(
  coords: { lat: number; lng: number }[],
): Promise<(CurrentWeather | null)[]> {
  if (coords.length === 0) return [];

  const chunks: { lat: number; lng: number }[][] = [];
  for (let i = 0; i < coords.length; i += WEATHER_CHUNK) {
    chunks.push(coords.slice(i, i + WEATHER_CHUNK));
  }

  try {
    const results = await Promise.all(chunks.map((chunk) => fetchWeatherChunk(chunk)));
    return results.flat();
  } catch (err) {
    console.warn("geo: weather fetch failed.", err);
    return coords.map(() => null);
  }
}
